import * as React from 'react';
import { assign as _assign, includes as _includes } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { ConfigurationModel, AssemblyAccessoriesModel, AssemblyAccessoriesDefaultModel, CONSOLE_TYPES } from 'franke-trough-dependencies';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { requestAPIActions } from 'loopback-react-request';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Assemblies.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';
import ConsolesOverlapChoice from './ConsoleOverlapChoice';

export interface AssemblyAccessoriesProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: Function;
}

export interface AssemblyAccessoriesState {
  console?: CONSOLE_TYPES;
  dropdownOpen: boolean;
}

export const AssemblyAccessoriesLabel: React.SFC<{value: CONSOLE_TYPES | 'null'}> = (props) => {
  switch (props.value) {
    case 'stainlessSteel':
      return (
        <span>
          <FormattedMessage {...MESSAGES.assemblyAccessoriesLabelStainlessSteel} />
        </span>
      );
    case 'stainlessSteelWhite':
      return (
        <span>
          <FormattedMessage {...MESSAGES.assemblyAccessoriesStainlessSteelWhite} />
        </span>
      );
    case 'miranit':
      return (
        <span>
          <FormattedMessage {...MESSAGES.assemblyAccessoriesLabelMiranit} />
        </span>
      );
    default:
      return null;
  }
}; 

export class AssemblyAccessoriesPartial extends React.Component<AssemblyAccessoriesProps, AssemblyAccessoriesState> {
  constructor(props: AssemblyAccessoriesProps) {
    super(props);
    this.state = {
      dropdownOpen: false,
    };
    this.handleClick = this.handleClick.bind(this);
    this.toggle = this.toggle.bind(this);
  }

  componentDidMount() {
    if (!this.console) {
      return;
    }

    this.setState({
      console: this.console,
    });
  }

  componentDidUpdate(prevProps: AssemblyAccessoriesProps) {
    const prevConfiguration = prevProps.configuration.result;

    if (
      (this.configuration && prevConfiguration) && 
      (this.configuration.updated_at !== prevConfiguration.updated_at)  
    ) {
      if (!this.console) { return; }
      this.setState({
        console: this.console,
      });
    }
  }

  public render() {
    const selected = this.state.console || this.console || (this.consoleDefaults && this.consoleDefaults.default) || 'stainlessSteel';
    return (
      <div className="position-relative partial-assembly-accessories pb-4">
        <ConsolesOverlapChoice id="apronOverlap" />
        <Dropdown
          className="dropdown-select"
          disabled={this.disabled}
          isOpen={this.state.dropdownOpen} 
          toggle={this.toggle}
        >
          <DropdownToggle
            caret={true} 
            disabled={this.disabled}
          >
            <AssemblyAccessoriesLabel value={selected} />
          </DropdownToggle>
          <DropdownMenu>
            {this.consoleDefaults && this.consoleDefaults.allValues && this.consoleDefaults.allValues.map((value, index) => {
              const key = `console_key_${index}`;
              const disabled = !(_includes(this.consoleDefaults && this.consoleDefaults.values, value))

              return (
                <DropdownItem
                  key={key} 
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => this.handleClick(event, value)}
                  disabled={this.disabled || value === selected || disabled}
                >
                  <AssemblyAccessoriesLabel value={value} />
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </Dropdown>
        <ul className="list-unstyled">
          <li><FontAwesomeIcon icon={faCheck} /> <FormattedMessage {...MESSAGES.assemblyAccessoriesListConsole} /></li>
          <li><FontAwesomeIcon icon={faCheck} /> <FormattedMessage {...MESSAGES.assemblyAccessoriesListTriggerProtection} /></li>
          <li><FontAwesomeIcon icon={faCheck} /> <FormattedMessage {...MESSAGES.assemblyAccessoriesListAngleBar} /></li>
          <li><FontAwesomeIcon icon={faCheck} /> <FormattedMessage {...MESSAGES.assemblyAccessoriesListFixingMaterial} /></li>
        </ul>
      </div>
    );
  }

  private dispatchConfiguration(console?: CONSOLE_TYPES) {
    if (!this.assemblyAccessories || this.disabled) { return; }
  
    const assemblyAccessories: AssemblyAccessoriesModel = this.assemblyAccessories as AssemblyAccessoriesModel;
    const data: AssemblyAccessoriesModel = _assign({}, assemblyAccessories, {
      console,
    });
    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {assemblyAccessories: data}
        },
      },
      true,
    ));
  }

  private handleClick(event: React.MouseEvent<HTMLButtonElement>, value: CONSOLE_TYPES): void {
    this.setState({
      console: value,
      dropdownOpen: false,
    });
    this.dispatchConfiguration(value);
  }

  private get assemblyAccessories(): AssemblyAccessoriesModel | undefined {
    return this.configuration && this.configuration.assemblyAccessories;
  }

  private get configuration(): ConfigurationModel | undefined{
    return this.props.configuration && this.props.configuration.result;
  }

  private get console(): AssemblyAccessoriesModel['console'] | undefined {
    return this.assemblyAccessories && this.assemblyAccessories.console;
  }
  
  private get consoleDefaults(): AssemblyAccessoriesDefaultModel['console'] | undefined {
    return this.configuration && this.configuration.defaults.assemblyAccessories.console;
  }
  
  private get disabled(): boolean {
    return !isConfigurationEditable();
  }

  private toggle() {
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen
    }));
  }
}

export const AssemblyAccessories = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
}))(AssemblyAccessoriesPartial);

export default AssemblyAccessories;